import { AddIcon, ReloadIcon } from '@approvalmax/ui';
import { Box, Button, Collapse, Flex, Grid, Table, Tabs } from '@approvalmax/ui/src/components';
import { PageWrapper } from '@approvalmax/ui/src/components';
import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { FC, memo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CustomFieldType } from 'shared/constants';

import { mapSelectNetSuiteFieldAnswerToState } from './NetSuiteCustomFields.helpers';
import { useCustomFieldsTable, useSave } from './NetSuiteCustomFields.hooks';
import { messages } from './NetSuiteCustomFields.messages';
import { TableArea } from './NetSuiteCustomFields.styles';
import { NetSuiteCustomFieldsProps } from './NetSuiteCustomFields.types';

export const NetSuiteCustomFields: FC<NetSuiteCustomFieldsProps> = memo((props) => {
    const { company, customFieldReference, customFieldList } = props;

    const companyTemplates = useSelector((state) => selectors.template.getTemplatesByCompanyId(state, company.id));

    const [showErrors, setShowErrors] = useState(false);

    const {
        customFieldsState: bodyFieldsState,
        onCustomFieldAdd: onBodyCustomFieldAdd,
        onCustomFieldReorder: onBodyCustomFieldReorder,
        tableDef: bodyFieldsTableDef,
    } = useCustomFieldsTable({
        company,
        showErrors,
        type: CustomFieldType.Body,
        initializer: () =>
            mapSelectNetSuiteFieldAnswerToState(
                customFieldList,
                customFieldReference,
                companyTemplates,
                CustomFieldType.Body
            ),
        availableFields: customFieldReference || [],
    });

    const {
        customFieldsState: lineFieldsState,
        onCustomFieldAdd: onLineCustomFieldAdd,
        onCustomFieldReorder: onLineCustomFieldReorder,
        tableDef: lineFieldsTableDef,
    } = useCustomFieldsTable({
        company,
        showErrors,
        type: CustomFieldType.Line,
        initializer: () =>
            mapSelectNetSuiteFieldAnswerToState(
                customFieldList,
                customFieldReference,
                companyTemplates,
                CustomFieldType.Line
            ),
        availableFields: customFieldReference || [],
    });

    const {
        customFieldsState: segmentFieldsState,
        onCustomFieldAdd: onSegmentCustomFieldAdd,
        onCustomFieldReorder: onSegmentCustomFieldReorder,
        tableDef: segmentFieldsTableDef,
    } = useCustomFieldsTable({
        company,
        showErrors,
        type: CustomFieldType.Segment,
        initializer: () =>
            mapSelectNetSuiteFieldAnswerToState(
                customFieldList,
                customFieldReference,
                companyTemplates,
                CustomFieldType.Segment
            ),
        availableFields: customFieldReference || [],
    });

    const { onSave, areFieldsUpdating } = useSave({
        company,
        bodyFieldsState,
        lineFieldsState,
        segmentFieldsState,
        setShowErrors,
    });

    return (
        <PageWrapper>
            <Helmet
                title={messages.documentTitle({
                    companyName: company.displayName,
                })}
            />

            <PageWrapper.Toolbar bordered spacingBottom={0}>
                <Flex justifyContent='space-between' alignItems='start'>
                    <Tabs>
                        <Tabs.List>
                            <Tabs.Tab label={messages.tabName} />
                        </Tabs.List>
                    </Tabs>

                    <Button
                        size='small'
                        color='blue80'
                        startIcon={<ReloadIcon />}
                        onClick={onSave}
                        progress={areFieldsUpdating}
                        variant='outline'
                    >
                        {messages.publishChangesBtnText}
                    </Button>
                </Flex>
            </PageWrapper.Toolbar>

            <PageWrapper.Content>
                {/* two Grid for scrolling in the table */}
                <Grid>
                    <Grid gap={24} gridTemplateColumns='100%' overflowX='auto'>
                        <Collapse name={messages.bodyFieldsSectionHeader} bordered radius='small'>
                            <Box spacing='0 64'>
                                <TableArea>
                                    <Table
                                        bordered
                                        data={bodyFieldsState}
                                        reorderable
                                        hoverable={false}
                                        columns={bodyFieldsTableDef}
                                        onReorderRows={onBodyCustomFieldReorder}
                                    />
                                </TableArea>
                            </Box>

                            <Box spacing='8 24 16'>
                                <Button
                                    size='small'
                                    color='blue80'
                                    onClick={onBodyCustomFieldAdd}
                                    startIcon={<AddIcon />}
                                >
                                    {messages.addLine}
                                </Button>
                            </Box>
                        </Collapse>

                        <Collapse name={messages.lineFieldsSectionHeader} bordered radius='small'>
                            <Box spacing='0 64'>
                                <TableArea>
                                    <Table
                                        bordered
                                        hoverable={false}
                                        data={lineFieldsState}
                                        columns={lineFieldsTableDef}
                                        reorderable
                                        onReorderRows={onLineCustomFieldReorder}
                                    />
                                </TableArea>
                            </Box>

                            <Box spacing='8 24 16'>
                                <Button
                                    size='small'
                                    color='blue80'
                                    onClick={onLineCustomFieldAdd}
                                    startIcon={<AddIcon />}
                                >
                                    {messages.addLine}
                                </Button>
                            </Box>
                        </Collapse>

                        <Collapse name={messages.segmentFieldsSectionHeader} bordered radius='small'>
                            <Box spacing='0 64'>
                                <TableArea>
                                    <Table
                                        bordered
                                        hoverable={false}
                                        data={segmentFieldsState}
                                        columns={segmentFieldsTableDef}
                                        reorderable
                                        onReorderRows={onSegmentCustomFieldReorder}
                                    />
                                </TableArea>
                            </Box>

                            <Box spacing='8 24 16'>
                                <Button
                                    size='small'
                                    color='blue80'
                                    onClick={onSegmentCustomFieldAdd}
                                    startIcon={<AddIcon />}
                                >
                                    {messages.addLine}
                                </Button>
                            </Box>
                        </Collapse>
                    </Grid>
                </Grid>
            </PageWrapper.Content>
        </PageWrapper>
    );
});

NetSuiteCustomFields.displayName = 'NetSuiteCustomFields';
